import { useMemo, useState, type ReactElement } from "react";
import { Check, Copy } from "lucide-react";
import { IconButton, Stack, Surface, Text, TextInput } from "@ux/primitives";
import { palette, space } from "@ux/tokens";
import { copyToClipboard } from "../common/clipboard";
import type { WorkspaceView } from "./types";

export interface AvailableWorkspacesPanelProps {
    readonly workspaces: readonly WorkspaceView[];
    readonly columnName?: string;
}

/**
 * Reference panel showing active workspaces with a one-click copy on
 * each id. Lives next to the asset bulk-import file picker so the
 * operator can fill `assignedToWorkspaceId` without bouncing to the
 * Workspaces list.
 *
 * Tenants can have hundreds of desks, so the list is filterable and
 * capped at a handful of visible rows until the operator types.
 */
export function AvailableWorkspacesPanel({
    workspaces,
    columnName = "assignedToWorkspaceId",
}: AvailableWorkspacesPanelProps): ReactElement | null {
    const [filter, setFilter] = useState("");
    const active = useMemo(() => workspaces.filter((w) => !w.isDeleted), [workspaces]);

    const filtered = useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (q === "") return active;
        return active.filter((w) => {
            const haystack = `${w.name} ${w.friendlyName ?? ""} ${w.type} ${w.id}`.toLowerCase();
            return haystack.includes(q);
        });
    }, [active, filter]);

    if (active.length === 0) return null;

    const VISIBLE_CAP = 8;
    const visible = filtered.slice(0, VISIBLE_CAP);
    const hiddenCount = filtered.length - visible.length;

    return (
        <Surface tone="raised" padding={4}>
            <Stack gap={3}>
                <Stack gap={1}>
                    <Text role="eyebrow">Available workspaces</Text>
                    <Text role="muted">
                        For <code>Assign</code>, put a workspace name, number, or id in{" "}
                        <code>{columnName}</code>. Copy an id below when two workspaces share a
                        name. Leave the assignment columns blank for <code>Preserve</code> or{" "}
                        <code>Release</code>.
                    </Text>
                </Stack>
                {active.length > VISIBLE_CAP ? (
                    <div style={{ maxWidth: 360 }}>
                        <TextInput
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            placeholder="Filter by name, type, or id…"
                        />
                    </div>
                ) : null}
                <Stack gap={2}>
                    {visible.map((w) => (
                        <WorkspaceReferenceRow key={w.id} workspace={w} />
                    ))}
                </Stack>
                {hiddenCount > 0 ? (
                    <Text role="muted">{hiddenCount} more. Type to narrow the list.</Text>
                ) : null}
                {filtered.length === 0 ? (
                    <Text role="muted">No workspaces match that filter.</Text>
                ) : null}
            </Stack>
        </Surface>
    );
}

function WorkspaceReferenceRow({ workspace }: { readonly workspace: WorkspaceView }): ReactElement {
    const [copied, setCopied] = useState(false);

    async function handleCopy(): Promise<void> {
        const ok = await copyToClipboard(workspace.id);
        if (!ok) return;
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
    }

    return (
        <Stack
            direction="row"
            align="center"
            justify="space-between"
            gap={3}
            style={{
                padding: `${space[2]} ${space[3]}`,
                background: palette.surfaceInset,
                borderRadius: 6,
            }}
        >
            <Stack gap={0}>
                <Text role="body" weight="semibold">
                    {workspace.name}
                    {workspace.friendlyName !== null &&
                    workspace.friendlyName !== undefined &&
                    workspace.friendlyName !== ""
                        ? ` (${workspace.friendlyName})`
                        : ""}{" "}
                    · {workspace.type}
                </Text>
                <Text role="muted">
                    <code style={{ fontSize: "0.85em" }}>{workspace.id}</code>
                </Text>
            </Stack>
            <IconButton
                tone="neutral"
                label={copied ? "Copied" : "Copy workspace id"}
                icon={copied ? <Check size={14} /> : <Copy size={14} />}
                onClick={() => {
                    void handleCopy();
                }}
            />
        </Stack>
    );
}
